<template>
  <a
    v-if="article.metadata?.sourceItemIdAtOrigin !== entity.referredId"
    class="type--reading"
    :id="id"
    data-tooltip-template="articlePreview"
    :data-tooltip-content="JSON.stringify(tooltipContent)"
    @mouseenter="searchArticles"
    @click="openArticle"
    ><slot></slot
  ></a>
  <template v-else>
    <slot></slot>
  </template>
</template>

<script setup lang="ts">
import { useApi } from "@root/api/api";
import { useWorkspacesStore } from "@root/store/modules/workspaces";
import { useTooltip } from "@root/tooltips/useTooltip";
import type { MonitioAPI } from "@root/types.api.local";
import { delay } from "lodash-es";
import { computed, ref, unref } from "vue";
import { useRoute, useRouter } from "vue-router";

interface Props {
  id: number;
  originalStartPos: number;
  originalEndPos: number;
  class?: string;
  dir: string | undefined;
  entity: MonitioAPI.EntityDTO;
  article: MonitioAPI.SearchDocumentDTO;
}

const props = defineProps<Props>();
const workspacesStore = useWorkspacesStore();
const { updateTooltipContent } = useTooltip();
const route = useRoute();
const { push } = useRouter();
const { api } = useApi();

const workspaceConfig = ref(workspacesStore.currentWorkspaceConfig);
const workspaceId = ref(workspaceConfig.value.id);
const viewId = computed(
  () => (route.params.viewId as string) ?? workspaceConfig.value.baseViewId
);

const id = computed(() => `legix-link-${props.id}`);
const tooltipContent = ref({
  title: "Aguarde para ver ou carregue para abrir.",
  text: "",
});

const searchArticles = async () => {
  if (!api.search) return;
  if (tooltipContent.value.text !== "") return;

  let result: MonitioAPI.SearchDocumentDTO[] | null | undefined = undefined;
  if (props.entity.referredId) {
    try {
      const remote = await api.search?.search(
        viewId.value,
        {
          timeFrame: "all" as MonitioAPI.TimeFrame,
          isRelative: true,
        },
        0,
        1,
        [
          {
            facets: [
              {
                value: "sourceItemIdAtOrigin",
                query: [
                  {
                    value: props.entity.referredId,
                    negated: false,
                    propertyType: "sourceItemIdAtOrigin",
                  },
                ],
                mode: "singleType" as MonitioAPI.FilterGroupMode.SingleType,
              },
            ],
          },
        ],
        "relevance",
        "all",
        true
      );

      result = remote.data.result?.documents;
    } catch (error) {
      console.warn(
        "Failed to retrieve document with sourceItemIdAtOrigin: ",
        props.entity.referredId,
        "\n Falling back to standard searchs"
      );
    }
  }

  if (result === undefined || result?.length === 0) {
    try {
      const remote = await api.search?.search(
        viewId.value,
        {
          timeFrame: "all" as MonitioAPI.TimeFrame,
          isRelative: true,
        },
        0,
        1,
        [
          {
            facets: [
              {
                value: "customQuery",
                query: [
                  {
                    value: props.entity.value ?? props.entity.label,
                    negated: false,
                    propertyType: "customQuery",
                  },
                ],
                mode: "singleType" as MonitioAPI.FilterGroupMode.SingleType,
              },
            ],
          },
        ],
        "relevance",
        "all",
        true
      );

      result = remote.data.result?.documents;
    } catch (error) {
      console.warn("Something went wrong with the standart search");
    }
  }

  if (result === undefined || result?.length === 0) {
    tooltipContent.value.title = "Não foram encontrados documentos";
  } else {
    const title =
      result!.at(0)?.translations?.[result!.at(0)!.documentLang!].title ?? "";

    const legixSections =
      props.article?.metadata?.legixMetadata?.legixFormatting;

    const summaryStart = legixSections?.["summary"].at(0)?.start ?? 0;
    const summaryEnd = legixSections?.["summary"].at(0)?.end ?? 200;
    const text =
      result!
        .at(0)
        ?.translations?.[result!.at(0)!.documentLang!].mainText?.slice(
          summaryStart,
          summaryEnd
        ) ?? "";

    if (result?.length === 1) {
      tooltipContent.value.title = title;
      tooltipContent.value.text = text;
    } else {
      tooltipContent.value.title = title;
      tooltipContent.value.text = text;
    }
  }

  delay(() => updateTooltipContent(document.getElementById(id.value)!), 100);
};

const openArticle = () => {
  console.log("opening");

  push({
    name: "article",
    params: {
      viewId: viewId.value ?? unref(workspaceConfig.value.baseViewId),
      articleId: props.article.id,
    },
    query: route.query,
  });
};
</script>
